import {
  hasPermission,
  OXIDE_PLUGIN_MAX_BYTES,
  oxidePluginListSchema,
  oxidePluginNameSchema,
  oxidePluginSchema,
  type OxidePluginList,
} from '@hearth/shared';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { FileCode2, FolderOpen, Loader2, Puzzle, Trash2, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { Confirm } from '@/components/confirm';
import { EmptyState } from '@/components/page-header';
import { QueryState } from '@/components/query-state';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ApiRequestError, apiRequest } from '@/lib/api';
import { errorMessage } from '@/lib/forms';
import { bytes, timeAgo } from '@/lib/format';
import { serverQuery } from '@/lib/server-queries';

export const Route = createFileRoute('/_app/server/$server/oxide')({ component: OxideTab });

const pluginsQuery = (server: string) =>
  queryOptions({
    queryKey: ['server', server, 'oxide-plugins'],
    queryFn: ({ signal }) =>
      apiRequest(`/api/client/servers/${server}/oxide/plugins`, {
        schema: oxidePluginListSchema,
        signal,
      }),
  });

/**
 * Rust plugins: the .cs files in Oxide's (or Carbon's) plugins folder. Upload and remove
 * only; there's no uMod search because uMod has no stable public API (docs/DESIGN.md §12).
 */
function OxideTab() {
  const { server: id } = Route.useParams();
  const { data: server } = useSuspenseQuery(serverQuery(id));
  const list = useQuery(pluginsQuery(id));
  const can = (p: Parameters<typeof hasPermission>[1]) => hasPermission(server.permissions, p);
  return (
    <QueryState query={list}>
      {(data) =>
        data.folder === null ? (
          <EmptyState icon={Puzzle} title="Plugins need Oxide or Carbon">
            This server runs vanilla Rust
            {data.framework ? (
              <>
                {' '}
                (<code className="font-mono">FRAMEWORK</code> is “{data.framework}”)
              </>
            ) : null}
            . Set <code className="font-mono">FRAMEWORK</code> to oxide or carbon on the{' '}
            {can('startup.read') ? (
              <Link to="/server/$server/startup" params={{ server: id }} className="underline">
                Startup tab
              </Link>
            ) : (
              'Startup tab'
            )}{' '}
            and restart; the egg’s image installs the framework when the server starts.
          </EmptyState>
        ) : (
          <Plugins
            server={id}
            data={{ ...data, folder: data.folder }}
            canUpload={can('plugin.install')}
            canReplace={can('plugin.install') && can('plugin.remove')}
            canDelete={can('plugin.remove')}
            canBrowse={can('file.read')}
          />
        )
      }
    </QueryState>
  );
}

function Plugins({
  server,
  data,
  canUpload,
  canReplace,
  canDelete,
  canBrowse,
}: {
  server: string;
  data: OxidePluginList & { folder: string };
  canUpload: boolean;
  canReplace: boolean;
  canDelete: boolean;
  canBrowse: boolean;
}) {
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [replacing, setReplacing] = useState<File | null>(null);
  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: pluginsQuery(server).queryKey });

  const upload = useMutation({
    mutationFn: async ({ file, replace }: { file: File; replace: boolean }) =>
      apiRequest(
        `/api/client/servers/${server}/oxide/plugins/${encodeURIComponent(file.name)}${replace ? '?replace=true' : ''}`,
        { method: 'PUT', body: await file.text(), textBody: true, schema: oxidePluginSchema },
      ),
    onSuccess: (p) => {
      toast.success(`${p.name} uploaded. The framework loads it on its own.`);
      refresh();
    },
    onError: (e, { file, replace }) => {
      if (!replace && e instanceof ApiRequestError && e.body?.code === 'exists' && canReplace)
        setReplacing(file);
      else toast.error(errorMessage(e));
    },
  });
  const remove = useMutation({
    mutationFn: (name: string) =>
      apiRequest(`/api/client/servers/${server}/oxide/plugins/${encodeURIComponent(name)}`, {
        method: 'DELETE',
        schema: z.null(),
      }),
    onSuccess: (_, name) => {
      toast.success(`${name} removed.`);
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const pick = (files: FileList | null) => {
    for (const file of Array.from(files ?? [])) {
      if (!oxidePluginNameSchema.safeParse(file.name).success) {
        toast.error(`${file.name}: plugins are single .cs files.`);
        continue;
      }
      if (file.size > OXIDE_PLUGIN_MAX_BYTES) {
        toast.error(`${file.name} is larger than ${bytes(OXIDE_PLUGIN_MAX_BYTES)}.`);
        continue;
      }
      upload.mutate({ file, replace: false });
    }
    if (input.current) input.current.value = '';
  };

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-muted-foreground text-sm">
          {data.framework === 'carbon' ? 'Carbon' : 'Oxide'} plugins in{' '}
          <code className="font-mono">{data.folder}</code>. New and changed files are loaded without
          a restart. Find plugins on{' '}
          <a href="https://umod.org/plugins" target="_blank" rel="noreferrer" className="underline">
            umod.org
          </a>{' '}
          and upload the .cs file here.
        </p>
        <div className="flex gap-2">
          {canBrowse && (
            <Button asChild variant="outline">
              <Link to="/server/$server/files" params={{ server }} search={{ dir: data.folder }}>
                <FolderOpen /> Open folder
              </Link>
            </Button>
          )}
          {canUpload && (
            <>
              <input
                ref={input}
                type="file"
                accept=".cs"
                multiple
                hidden
                aria-label="Plugin files"
                onChange={(e) => pick(e.target.files)}
              />
              <Button onClick={() => input.current?.click()} disabled={upload.isPending}>
                {upload.isPending ? <Loader2 className="animate-spin" /> : <Upload />} Upload .cs
              </Button>
            </>
          )}
        </div>
      </div>

      {!data.folderExists && (
        <Alert>
          <Puzzle />
          <AlertTitle>The plugins folder doesn’t exist yet</AlertTitle>
          <AlertDescription>
            It appears when the server first starts with {data.framework ?? 'Oxide'} installed.
            Uploading a plugin creates it now.
          </AlertDescription>
        </Alert>
      )}

      {data.plugins.length === 0 ? (
        <EmptyState icon={FileCode2} title="No plugins installed">
          Upload a plugin’s .cs file to install it.
        </EmptyState>
      ) : (
        <Card className="gap-0 overflow-hidden p-0">
          <ul className="divide-y">
            {data.plugins.map((p) => (
              <li key={p.name} className="flex items-center gap-3 px-4 py-2.5">
                <FileCode2 className="text-game size-4 shrink-0" aria-hidden />
                <span className="min-w-0 flex-1 truncate font-medium">{p.name}</span>
                <span className="text-muted-foreground hidden text-xs tabular-nums sm:inline">
                  {bytes(p.size)} · {timeAgo(p.modifiedAt)}
                </span>
                {canDelete && !p.denied && (
                  <Confirm
                    title={`Remove ${p.name}?`}
                    description="The file is deleted and the framework unloads the plugin. Its config and data files stay."
                    action="Remove"
                    destructive
                    onConfirm={() => remove.mutate(p.name)}
                  >
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Remove ${p.name}`}
                      disabled={remove.isPending}
                    >
                      <Trash2 />
                    </Button>
                  </Confirm>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}

      <AlertDialog open={replacing !== null} onOpenChange={(o) => !o && setReplacing(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Replace {replacing?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              A plugin with this name is already installed. The framework reloads it with the new
              file.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (replacing) upload.mutate({ file: replacing, replace: true });
                setReplacing(null);
              }}
            >
              Replace
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
